import type { CSSProperties, MouseEvent as ReactMouseEvent } from 'react';
import { useMemo } from 'react';

import { makeRGB } from '@trello/colors';
import type { EmojiMartEmoji } from '@trello/emoji';
import { currentLocale } from '@trello/locale';
import { getTokenValue } from '@trello/theme';

import { EmojiProviderComponent } from './EmojiProviderComponent';
import { LazyEmojiMartPicker } from './LazyEmojiMartPicker';

import styles from './EmojiPicker.less';

export interface EmojiPickerProps {
  autoFocus?: boolean;
  dynamicWidth?: boolean;
  emojiButtonRadius?: string;
  emojiButtonSize?: number;
  emojiSize?: number;
  getSpritesheetURL?: (set: 'twitter', sheetSize?: 16 | 20 | 32 | 64) => string;
  icons?: 'auto' | 'outline' | 'solid';
  locale?: string;
  maxFrequentRows?: number;
  onEmojiSelect?(emoji: EmojiMartEmoji, e: ReactMouseEvent): void;
  set?: string;
  theme?: 'dark' | 'light';
}

export const EmojiPicker = ({
  autoFocus = true,
  dynamicWidth = false,
  emojiButtonRadius = '3px',
  emojiButtonSize = 42,
  emojiSize = 28,
  maxFrequentRows = 2,
  icons = 'outline',
  set = 'twitter',
  theme = 'light',
  ...props
}: EmojiPickerProps) => {
  const emojiMartCustomStyles = useMemo(() => {
    const hexColors = {
      background: getTokenValue('elevation.surface.overlay', '#FFFFFF'),
      text: getTokenValue('color.text', '#172B4D'),
      input: getTokenValue('color.background.input', '#FFFFFF'),
    };

    return {
      '--rgb-background': makeRGB(hexColors.background),
      '--rgb-color': makeRGB(hexColors.text),
      '--rgb-input': makeRGB(hexColors.input),
    } as CSSProperties;
  }, []);

  return (
    <div className={styles.emojiPicker} style={emojiMartCustomStyles}>
      <EmojiProviderComponent>
        {(sheet, backgroundImageFn) => {
          if (sheet) {
            return (
              <LazyEmojiMartPicker
                {...props}
                autoFocus={autoFocus}
                dynamicWidth={dynamicWidth}
                emojiButtonRadius={emojiButtonRadius}
                emojiButtonSize={emojiButtonSize}
                emojiSize={emojiSize}
                maxFrequentRows={maxFrequentRows}
                icons={icons}
                set={set}
                theme={theme}
                locale={currentLocale.slice(0, 2)}
                getSpritesheetURL={backgroundImageFn}
              />
            );
          }
          return null;
        }}
      </EmojiProviderComponent>
    </div>
  );
};
